import React from "react";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";

const toneMap = (theme) => ({
    neutral: { color: theme.palette.text.primary, accent: theme.palette.divider },
    warn: { color: theme.palette.warning.dark, accent: theme.palette.warning.main },
    error: { color: theme.palette.error.dark, accent: theme.palette.error.main },
    info: { color: theme.palette.primary.dark, accent: theme.palette.primary.main },
    success: { color: theme.palette.success.dark, accent: theme.palette.success.main },
    muted: { color: theme.palette.text.secondary, accent: theme.palette.divider },
});

const Root = styled(Box, {
    shouldForwardProp: (prop) => !["$tone", "$size", "$accent", "$clickable"].includes(prop),
})(({ theme, $tone, $size, $accent, $clickable }) => {
    const tones = toneMap(theme);
    const tone = tones[$tone] || tones.neutral;
    return {
        position: "relative",
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.25),
        cursor: $clickable ? "pointer" : "default",
        transition: theme.transitions.create(["box-shadow", "border-color"]),
        "&::before": $accent
            ? {
                  content: '""',
                  position: "absolute",
                  top: theme.spacing(1.25),
                  bottom: theme.spacing(1.25),
                  left: 0,
                  width: 3,
                  borderRadius: 2,
                  background: tone.accent,
              }
            : undefined,
        "&:hover": $clickable ? { boxShadow: theme.shadows[2] } : undefined,
        "& .stat-header": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
        },
        "& .stat-label": {
            fontSize: theme.typography.size.xs,
            fontWeight: theme.typography.fontWeightSemiBold,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: theme.palette.text.secondary,
        },
        "& .stat-icon": {
            display: "flex",
            alignItems: "center",
            color: theme.palette.text.secondary,
        },
        "& .stat-value": {
            fontSize: $size === "large" ? theme.typography.size.xxxl : theme.typography.size.xl,
            fontWeight: theme.typography.fontWeightSemiBold,
            lineHeight: 1.1,
            color: tone.color,
            fontVariantNumeric: "tabular-nums",
        },
        "& .stat-sub": {
            fontSize: theme.typography.size.sm,
            color: theme.palette.text.secondary,
        },
    };
});

const StatCard = ({ label, value, sub, tone = "neutral", icon, size = "compact", accent, onClick }) => {
    const showAccent = accent !== undefined ? accent : size === "compact";
    return (
        <Root
            $tone={tone}
            $size={size}
            $accent={showAccent}
            $clickable={!!onClick}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={
                onClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick(e);
                          }
                      }
                    : undefined
            }
        >
            <Box className="stat-header">
                <Typography component="span" className="stat-label">
                    {label}
                </Typography>
                {icon && <span className="stat-icon">{icon}</span>}
            </Box>
            <Typography component="span" className="stat-value">
                {value}
            </Typography>
            {sub && (
                <Typography component="span" className="stat-sub">
                    {sub}
                </Typography>
            )}
        </Root>
    );
};

export default StatCard;
